import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { List } from 'immutable';
import { Adjustment } from '../models/adjustment';
import { AdjustmentHttpService } from './adjustment.http.service';
import { FlashMessagesService } from '../../shared/flash-messages/service/flash.messages.service';
import { ReplaySubject } from 'rxjs/ReplaySubject';
import { ReservationStore } from './reservation-store';
import { ReservationStatusCode } from '../models/reservation-status-code.enum';
import { map, take, tap } from 'rxjs/operators';
import { AdjustmentTypeStore } from './adjustment-type/adjustment-type.store';
import { AdjustmentSign } from '../models/adjustment-sign.enum';
import { RsvpStore } from './rsvp.store';
import { VendorsTotalPaymentsSummary } from '../models/vendors-total-payments-summary';
import Decimal from 'decimal.js';
import { PaymentSummaryStore } from './payment-summary/payment-summary.store';

@Injectable()
export class AdjustmentStore {

  private adjustmentList: ReplaySubject<List<Adjustment>> = new ReplaySubject<List<Adjustment>>(1);
  private adjustmentArray: ReplaySubject<Adjustment[]> = new ReplaySubject<Adjustment[]>(1);
  private vendorsTotalPayments: ReplaySubject<VendorsTotalPaymentsSummary> = new ReplaySubject<VendorsTotalPaymentsSummary>(1);

  constructor(
    private adjustmentHttpService: AdjustmentHttpService,
    private flashMessagesService: FlashMessagesService,
    private reservationStore: ReservationStore,
    private rsvpStore: RsvpStore,
    private adjustmentTypeStore: AdjustmentTypeStore,
    private paymentSummaryStore: PaymentSummaryStore
  ) { }

  addAdjustment(adjustment: Adjustment): Observable<Adjustment> {
    return this.adjustmentHttpService.addAdjustment(adjustment).pipe(
      tap((addedAdjustment: Adjustment) => {
        this.paymentSummaryStore.reloadPaymentSummary(adjustment.eventPartyId).take(1).subscribe();
        const adjustmentSign = adjustment.adjustmentType.credit ? AdjustmentSign.CREDIT : AdjustmentSign.DEBIT;
        this.adjustmentTypeStore.getAdjustmentType(addedAdjustment.adjustmentType.id, adjustmentSign)
          .take(1)
          .subscribe((adjustmentType) => {
            this.flashMessagesService.success(
              'EVENT.ADJUSTMENT.ADJUSTMENT_SAVED', null, {
                'type': adjustmentType.name,
                'description': addedAdjustment.description ? addedAdjustment.description : ''
              });
          });
      })
    )
      .catch(error => {
        this.flashMessagesService.error('MESSAGES.ERROR.ADD_ADJUSTMENT', `Error adding adjustment: ${error}`);
        return Observable.throw(error);
      });
  }

  get vendorsTotalPaymentsSummary(): Observable<VendorsTotalPaymentsSummary> {
    return this.vendorsTotalPayments.asObservable();
  }

  vendorAdjustments(eventPartyId: number): Observable<List<Adjustment>> {
    return this.adjustmentList.map((filteredList: List<Adjustment>) => {
      return filteredList.filter((filtered: Adjustment) => filtered.eventPartyId === eventPartyId).toList();
    });
  }

  public vendorAdjustmentsAsArray(eventPartyId: number): Observable<Adjustment[]> {
    return this.adjustmentArray
      .map(payments => payments != null ? payments.filter(payment => payment.eventPartyId === eventPartyId) : null);
  }

  loadAdjustments(eventId: number): Observable<List<Adjustment>> {
    return this.adjustmentHttpService.getAdjustments(eventId)
      .catch(error => {
        this.flashMessagesService.error('MESSAGES.ERROR.COULD_NOT_GET_PAYMENTS', `Error getting adjustments: ${error}`);
        return Observable.throw(error);
      })
      .map((result: List<Adjustment>) => {
        const sortedIterable = result.sort(
          (adjustment1, adjustment2) => {
            if (adjustment1.createdDate < adjustment2.createdDate) {
              return -1;
            } else if (adjustment1.createdDate > adjustment2.createdDate) {
              return 1;
            } else {
              return 0;
            }
          });
        this.adjustmentList.next(sortedIterable.toList());
        this.adjustmentArray.next(sortedIterable.toArray());
        return result;
      });
  }

  loadAdjustmentsByParty(eventId: number, eventPartyId: number): Observable<List<Adjustment>> {
    return this.adjustmentHttpService.getAdjustmentsByParty(eventId, eventPartyId)
      .catch(error => {
        this.flashMessagesService.error('MESSAGES.ERROR.COULD_NOT_GET_PAYMENTS', `Error getting adjustments: ${error}`);
        return Observable.throw(error);
      })
      .map((result: List<Adjustment>) => {
        const sortedIterable = result.sort(
          (adjustment1, adjustment2) => {
            if (adjustment1.createdDate < adjustment2.createdDate) {
              return -1;
            } else if (adjustment1.createdDate > adjustment2.createdDate) {
              return 1;
            } else {
              return 0;
            }
          });
        this.adjustmentList.next(sortedIterable.toList());
        this.adjustmentArray.next(sortedIterable.toArray());
        return result;
      });
  }

  loadVendorsTotalPaymentsSummary(eventId: number): Observable<VendorsTotalPaymentsSummary> {
    return this.adjustmentHttpService.getVendorsTotalPayments(eventId)
      .catch((error: any) => {
        if (error.includes('Unable to determine Tax')) {
          this.flashMessagesService.error('MESSAGES.ERROR.GET_TAX_RATE', `Error getting TaxRate: ${error}`);
        } else if (error.includes('Unable to view Payment Page')) {
          this.flashMessagesService.error('MESSAGES.ERROR.TDS_HEALTH', `Error in TaxRate  Health: ${error}`);
        } else {
          // Catch error here so resolver also displays error.
          this.flashMessagesService.error('MESSAGES.ERROR.COULD_NOT_GET_VENDORS_TOTAL_PAYMENTS',
          `Error retrieving Vendors Total Payments: ${error}`);
        }
        return Observable.throw(error);
      })
      .map((res: any) => {
        this.vendorsTotalPayments.next(res);
        return res;
      });
  }

  loadVendorsTotalPaymentsSummaryByParty(eventId: number, eventPartyId: number): Observable<VendorsTotalPaymentsSummary> {
    return this.adjustmentHttpService.getVendorsTotalPaymentsByParty(eventId, eventPartyId)
      .catch((error: any) => {
        // Catch error here so resolver also displays error.
        this.flashMessagesService.error('MESSAGES.ERROR.COULD_NOT_GET_VENDORS_TOTAL_PAYMENTS',
        `Error retrieving Vendors Total Payments: ${error}`);
        return Observable.throw(error);
      })
      .map((res: any) => {
        this.vendorsTotalPayments.next(res);
        return res;
      });
  }

  deleteAdjustment(adjustmentId: number, eventId: number, eventPartyId: number): Observable<any> {
    return this.adjustmentHttpService.deleteAdjustment(adjustmentId)
      .catch((error: any) => {
        this.flashMessagesService.error('MESSAGES.ERROR.DELETE_ADJUSTMENT', `Error deleting adjustment: ${error}`);
        return Observable.of(error);
      })
      .map(res => {
        this.paymentSummaryStore.reloadPaymentSummary(eventPartyId).take(1).subscribe();
        this.loadAdjustments(eventId).subscribe();
        this.loadVendorsTotalPaymentsSummary(eventId).pipe(take(1)).subscribe();
        return res;
      });
  }

  getVendorCalculatedPayment(eventPartyId: number, reservationStatusCode: ReservationStatusCode, subscriber: boolean,
    tax?: number): Observable<number> {
    let totalCommissions = new Decimal(0);
    const obs$ = subscriber ?
      this.reservationStore.getVendorTotalCommissions(reservationStatusCode, eventPartyId) :
      this.rsvpStore.vendorTotalCommission(eventPartyId);

    return obs$.flatMap(total => {
      totalCommissions = new Decimal(total);
      return this.vendorAdjustments(eventPartyId);
    }).map((list: List<Adjustment>) => {
      const adjustmentTotal: Decimal = list.reduce((sum: Decimal, adjustment: Adjustment) => {
        return sum.add(adjustment.adjustmentAmount);
      }, new Decimal(0));

      let calculatedPayment = adjustmentTotal.plus(totalCommissions);
      if (!calculatedPayment.isZero() && tax) {
        calculatedPayment = calculatedPayment.plus(new Decimal(tax));
      }

      return Number(calculatedPayment);
    });
  }

}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/qfGQY7pgg/1/oneonyx/grouppay-group/grouppay/pioneer-web/src/app/event/service/adjustment.store.ts